import { useState } from 'react'
import PropTypes from 'prop-types'
import Alert from '../furniture/Alert'
import ButtonEl from '../helpers/ctas/Button'
import { useMemberSession } from '../../helpers/memberSession'
import { getReceiptSuccessAlertProps } from '../../helpers/receiptPageConfig'

const ERROR_MESSAGES = {
  unauthenticated: 'You must be logged in to download your receipt.',
  unavailable: 'Your receipt is not yet available. Please try again shortly.',
  default: 'Unable to download your receipt. Please try again or contact support.',
}

/**
 * Post-payment confirmation page. Provides a button to download the paid tax-invoice PDF from
 * GET /umbraco/surface/receipt/download. NDS issues a single document serving as both the
 * receipt and the tax invoice, so the user-facing wording says "receipt".
 */
function ReceiptDownloadPage({ title, description, receiptDownloadUrl, receiptAlerts }) {
  const [downloading, setDownloading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const { status, loaded } = useMemberSession()
  const successAlert = getReceiptSuccessAlertProps(loaded ? status : null, receiptAlerts)

  const handleDownload = async () => {
    if (!receiptDownloadUrl) {
      setErrorMessage(ERROR_MESSAGES.default)
      return
    }

    setDownloading(true)
    setErrorMessage(null)

    try {
      const res = await fetch(receiptDownloadUrl, { credentials: 'same-origin' })

      if (res.status === 401) {
        setErrorMessage(ERROR_MESSAGES.unauthenticated)
        return
      }

      if (!res.ok) {
        const json = await res.json().catch(() => null)
        setErrorMessage(json?.error ?? ERROR_MESSAGES.unavailable)
        return
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'receipt.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[ReceiptDownloadPage] Download failed:', err)
      setErrorMessage(ERROR_MESSAGES.default)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="receipt-download-page container mx-auto px-5 py-10 xl:px-0">
      {title && <h1 className="text-midnight-500 mb-4 text-3xl font-semibold">{title}</h1>}
      {description && <p className="text-grey mb-8">{description}</p>}

      {successAlert && (
        <div className="mb-8">
          <Alert
            type="success"
            dismissible={false}
            title={successAlert.title}
            description={successAlert.description}
          />
        </div>
      )}

      {errorMessage && (
        <div className="receipt-download-page__error mb-6 rounded border border-red-300 bg-red-50 p-4" role="alert">
          <p className="text-red-700">{errorMessage}</p>
        </div>
      )}

      <p className="sr-only" role="status" aria-live="polite">
        {downloading ? 'Downloading your receipt, please wait…' : ''}
      </p>

      <ButtonEl
        type="button"
        theme="primary"
        icon="ArrowDownTray"
        position="After"
        onClick={handleDownload}
        disabled={downloading}
      >
        {downloading ? 'Downloading…' : 'Download receipt'}
      </ButtonEl>
    </div>
  )
}

ReceiptDownloadPage.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  receiptDownloadUrl: PropTypes.string,
  receiptAlerts: PropTypes.shape({
    renewal: PropTypes.shape({ title: PropTypes.string, description: PropTypes.string }),
    registration: PropTypes.shape({ title: PropTypes.string, description: PropTypes.string }),
    generic: PropTypes.shape({ title: PropTypes.string, description: PropTypes.string }),
  }),
}

export default ReceiptDownloadPage
